/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import { isObjectOrArrayAndEmpty } from '../../../shared/Utils/Utils';
import ConnectionError from '../../../shared/ErrorComponents/ConnectionError';
import withLoadingStatus from '../../../shared/StatusWrappers/LoadingStatus';
import withErrorStatus from '../../../shared/StatusWrappers/ErrorStatus';
import {
  IAny,
  IDetailComponentProps,
  IDetailComponentPropsSeparateAPI,
  IEmaasEnergyUsageComponentData,
  IEmaasEnergyUsageComponentDataArea
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import EmaasEnergyUsagesAllUtilityDetailsComponent from './EmaasEnergyUsagesAllUtilityDetailsComponent';
import EmaasNILMEnergyUsagesComponent from '../NILMEnergyUsage/EmaasNILMEnergyUsagesComponent';
import { Card, Icon, Tooltip } from '@scuf/common';
import EmaasEUsageAllUtilDetailsBasicComponent from './EmaasEUsageAllUtilDetailsBasicComponent';
import {
  EnergyUsageDataSourceType,
  EnergyUsageField
} from '../../../shared/Enums';
import EmaasNILMEnergyDisaggregationComponent from '../EnergyDisaggregationDetails/EmaasNILMEnergyDisaggregationComponent';
import { ApiContext } from '../../../../context/ApiContext';

type IEnergyData =
  | IEmaasEnergyUsageComponentData
  | IEmaasEnergyUsageComponentDataArea;

interface ITotalEmissionTabDetailsComponentProps {
  data: IEnergyData;
  onRefresh: () => Promise<void>;
  nilmTab?: string;
  forGraph?: boolean;
  forSummary?: boolean;
}

const DetermineNILM = (props: IAny) => {
  const { data, nilmTab } = props;
  return data.subscriptionType === EnergyUsageField.BASICSUBS &&
    data.addOn.includes('NILM') &&
    nilmTab === EnergyUsageField.NILMENERGYUSAGE ? (
    <EmaasNILMEnergyUsagesComponent data={data} />
  ) : (
    <EmaasEnergyUsagesAllUtilityDetailsComponent
      data={data as IEmaasEnergyUsageComponentDataArea}
      forSummary={props.forSummary}
      forGraph={props.forGraph}
    />
  );
};

const SiteEnergyAllUtilitiesDetailContent = (
  props: ITotalEmissionTabDetailsComponentProps
) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh, nilmTab } = props;
  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }
  return (
    <div className="carbondetail-performance-detail-container">
      <div>
        {data.subscriptionType === EnergyUsageField.BASICSUBS && !data.addOn ? (
          <EmaasEUsageAllUtilDetailsBasicComponent data={data} />
        ) : (
          <DetermineNILM
            forSummary={props.forSummary}
            forGraph={props.forGraph}
            data={data}
            nilmTab={nilmTab}
          />
        )}
        {/* {data.subscriptionType === EnergyUsageField.BASICSUBS && !data.addOn ? (
          <EmaasEUsageAllUtilDetailsBasicComponent data={data} />
        ) : data.subscriptionType === EnergyUsageField.BASICSUBS &&
          data.addOn &&
          data.addOn.includes('NILM') &&
          nilmTab === EnergyUsageField.NILMENERGYUSAGE ? (
          <EmaasNILMEnergyUsagesComponent data={data} />
        ) : (
          <EmaasEnergyUsagesAllUtilityDetailsComponent
            data={data as IEmaasEnergyUsageComponentDataArea}
          />
        )} */}
      </div>
    </div>
  );
};

const SiteEnergyAllUtilitiesDetailWithLoader = withLoadingStatus(
  withErrorStatus(SiteEnergyAllUtilitiesDetailContent)
);
//IDetailComponentPropsSeparateAPI

const EmaasEnergyUsagesAllUtilityDetailWidgetSeparateAPI = (
  props: IDetailComponentPropsSeparateAPI
) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const {
    dataConnector,
    nilmTab,
    requestContext,
    dataConnectorPerformanceAggregation
  } = props;

  const apiContext = useContext(ApiContext);
  const {
    dataSourceType,
    enableConsumptionSourceSelect,
    enableWaterUsageDashboards
  } = apiContext;
  const [currentTab, setCurrentTab] = useState<string>('energy');

  const onRefreshPerf = () =>
    dataConnectorPerformanceAggregation &&
    dataConnectorPerformanceAggregation.actions &&
    dataConnectorPerformanceAggregation.actions.getAll();

  const onRefresh = () =>
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();

  const allPerf = dataConnectorPerformanceAggregation.data.all;
  const loadingPerf = dataConnectorPerformanceAggregation.data.loading;
  const errorPerf = dataConnectorPerformanceAggregation.data.error;
  const statusPerf = dataConnectorPerformanceAggregation.data.status;

  const PerformanceData = (allPerf as unknown) as IEnergyData;
  const isDataAvailableForPerfromance =
    PerformanceData && PerformanceData.performanceAggregation ? true : false;

  const { all, loading, error, status } = dataConnector.data;
  const electricitySavings = (all as unknown) as IEnergyData;
  const isDataAvailable =
    electricitySavings && electricitySavings.summary ? true : false;

  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [isLoadingPerf, setIsLoadingPerf] = useState<boolean>(loadingPerf);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(errorPerf);
  const [statusCodePerf, setStatusCodePerf] = useState<number | null>(0);
  const [errorInfoPerf, setErrorInfoPerf] = useState(errorPerf);
  const [data, setData] = useState({} as IEnergyData);
  const [dataPerf, setDataPerf] = useState({} as IEnergyData);

  useEffect(() => {
    setIsLoadingPerf(loadingPerf);
    if (
      isDataAvailableForPerfromance ||
      errorPerf ||
      statusPerf ||
      !loadingPerf
    ) {
      if (isDataAvailableForPerfromance) {
        let tempData: any = PerformanceData;
        tempData.summary = {};
        setDataPerf(tempData);
      }
      setErrorInfoPerf(errorPerf);
      setStatusCodePerf(statusPerf);
    }
  }, [isDataAvailableForPerfromance, loadingPerf, statusPerf]);

  useEffect(() => {
    setIsLoading(loading);

    if (isDataAvailable || error || status || !loading) {
      if (isDataAvailable) {
        let tempData = electricitySavings;
        setData(tempData);
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status]);

  //   useEffect(() => {
  //     setIsLoading(loading);
  //     if ((isDataAvailable || error || status) && !loading) {
  //       if (isDataAvailable) {
  //         setData(electricitySavings);
  //       }
  //       setErrorInfo(error);
  //       setStatusCode(status);
  //     }
  //   }, [isDataAvailable, loading, status]);

  return (
    <>
      <Card className="emaas-performance-detail-card">
        <Card.Header>
          <div className="emaas-summary-card-header">
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span>{nilmTab ? t(d.NILMENERGYUSAGE) : props.headerTitle}</span>
              {nilmTab && (
                <span className="NILM-energy-usage-subheader">
                  {t(d.NILMENERGYUSAGESUBHEADER)}
                </span>
              )}
            </div>
            {!nilmTab && (
              <Tooltip
                element={
                  <Icon
                    root="common"
                    name="badge-info"
                    exactSize="0.875rem"
                    className="emaas-summary-card-header-info-icon"
                  />
                }
                event="hover"
                // content={t(d.EMAASENERGYUSAGETOOLTIP)}
                // content={
                //   data.subscriptionType === EnergyUsageField.BASICSUBS ||
                //   dataSourceType === EnergyUsageDataSourceType.UTILITY_METER
                //     ? t(d.EMAASENERGYUSAGETOOLTIPPREV)
                //     : props.headerTitle === t(d.WaterOverview)
                //     ? t(d.EMAASWATEROVERVIEWTOOLTIP)
                //     : t(d.EMAASENERGYUSAGETOOLTIP)
                // }

                content={
                  // tabName === 'allutilities'
                  data.subscriptionType === EnergyUsageField.BASICSUBS
                    ? t(d.EMAASPORTFOLIOENERGYUSAGETOOLTIPPREV)
                    : enableConsumptionSourceSelect
                    ? dataSourceType === EnergyUsageDataSourceType.UTILITY_METER
                      ? t(d.CEMSPORTFOLIO_DATA_SOURCE_UTIL_TOOLTIP)
                      : dataSourceType ===
                        EnergyUsageDataSourceType.UTILITY_INTERVAL
                      ? t(d.CEMSPORTFOLIO_DATA_SOURCE_INTERVAL_TOOLTIP)
                      : enableWaterUsageDashboards &&
                        props.headerTitle === t(d.WaterOverview)
                      ? t(d.CEMSPORTFOLIO_DATA_SOURCE_LIVE_WATER_TOOLTIP)
                      : t(d.CEMSPORTFOLIO_DATA_SOURCE_LIVE_TOOLTIP)
                    : enableWaterUsageDashboards
                    ? t(d.EMAASPORTFOLIOCONSUMPTIONTOOLTIP)
                    : t(d.EMAASPORTFOLIOENERGYUSAGETOOLTIP)
                }
                position="right center"
                size="small"
                header={props.headerTitle}
              />
            )}
          </div>
        </Card.Header>
        {statusCode == 204 || statusCodePerf == 204 ? (
          <Card.Content className="detail-summary-card detail-view ">
            <SiteEnergyAllUtilitiesDetailWithLoader
              loading={isLoading}
              error={errorInfo}
              statusCode={statusCode}
              onRefresh={onRefresh}
              data={data}
              loadingText={t(d.LOADING)}
              title={t(d.CONNECTIONERRORTITLE)}
              noDataMessage={t(d.NODATAAVAILABLE)}
              connectionErrorMessage={t(d.CONNECTIONERROR)}
              nilmTab={nilmTab}
              forSummary={true}
            />
          </Card.Content>
        ) : (
          <>
            <Card.Content className="detail-summary-card detail-view">
              <SiteEnergyAllUtilitiesDetailWithLoader
                loading={isLoading}
                error={errorInfo}
                statusCode={statusCode}
                onRefresh={onRefresh}
                data={data}
                loadingText={t(d.LOADING)}
                title={t(d.CONNECTIONERRORTITLE)}
                noDataMessage={t(d.NODATAAVAILABLE)}
                connectionErrorMessage={t(d.CONNECTIONERROR)}
                nilmTab={nilmTab}
                forSummary={true}
              />
            </Card.Content>
            <Card.Content className="detail-summary-card detail-view">
              <SiteEnergyAllUtilitiesDetailWithLoader
                loading={isLoadingPerf}
                error={errorInfoPerf}
                statusCode={statusCodePerf}
                onRefresh={onRefreshPerf}
                data={dataPerf}
                loadingText={t(d.LOADING)}
                title={t(d.CONNECTIONERRORTITLE)}
                noDataMessage={t(d.NODATAAVAILABLE)}
                connectionErrorMessage={t(d.CONNECTIONERROR)}
                nilmTab={nilmTab}
                forGraph={true}
              />
            </Card.Content>
          </>
        )}
      </Card>
    </>
  );
};
export default EmaasEnergyUsagesAllUtilityDetailWidgetSeparateAPI;
